import React, { useEffect, useRef, memo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { toPng } from 'html-to-image';
import { isEqual } from 'lodash';
import Chart from 'src/components/charts/Chart';
import { ChartConf } from 'src/components/charts/Chart/Chart';
import { ColorSet } from 'src/redux/reducers/theme';
import { captureException } from '@sentry/react';

const BATCH_SIZE = 10;

const Container = styled.div`
  z-index: -100;
  position: absolute;
  display: flex;
  flex-direction: column;
`;

const ChartDiv = styled.div`
  width: 900px;
`;

interface SnapChartImageV2Props {
  isLogarithmic: boolean;

  chartConf: {
    charts: ChartConf[];
  }[];

  onSnap: (data: string[]) => void;
  disabled: boolean;
  colors: ColorSet;
}

const captureRef = (ref: HTMLDivElement, colors: ColorSet) =>
  toPng(ref, {
    cacheBust: false,
    quality: 0.8,
    backgroundColor: colors.white,
    pixelRatio: 2,
    skipFonts: true,
  }).catch((err) => {
    captureException(err);
    return '';
  });

export const SnapChartImageV2: React.FC<SnapChartImageV2Props> = memo(
  ({ isLogarithmic, onSnap, disabled, chartConf, colors }) => {
    const chartRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [ready, setReady] = useState(false);
    const hasSnapped = useRef(false);

    const setChartRef = useCallback(
      (index: number) => (el: HTMLDivElement | null) => {
        chartRefs.current[index] = el;
      },
      [],
    );

    useEffect(() => {
      if (disabled) return;
      setReady(true);
    }, [disabled]);

    useEffect(() => {
      if (!ready || disabled || hasSnapped.current) return;

      const timer = setTimeout(async () => {
        hasSnapped.current = true;

        const refs = chartRefs.current;
        const results: string[] = [];

        // Process in batches to avoid overwhelming the browser
        for (let i = 0; i < refs.length; i += BATCH_SIZE) {
          const batch = refs.slice(i, i + BATCH_SIZE);
          const batchResults = await Promise.all(
            batch.map((ref) => (ref ? captureRef(ref, colors) : Promise.resolve(''))),
          );
          results.push(...batchResults);
        }

        onSnap(results);
      }, 150);

      return () => clearTimeout(timer);
    }, [ready, disabled]);

    if (!ready || disabled) {
      return null;
    }

    return (
      <Container>
        {chartConf.map((conf, idx) => (
          <ChartDiv key={idx} ref={setChartRef(idx)}>
            <Chart isLogarithmic={isLogarithmic} isPrintable input={conf.charts} forceLightMode />
          </ChartDiv>
        ))}
      </Container>
    );
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
);
